/**
 * Ted 10-09 — the Vaccination inbox shows its own forwarding address.
 *
 * Johnny: "very apparent, one click copy, a little call-out saying it's been
 * copied." The address used to appear only inside the EMPTY state, so the
 * moment one record was waiting, the desk could no longer find the address
 * to give a vet's office. It now heads the page, with a Copy button whose
 * confirmation is announced (aria-live) as well as shown.
 *
 * Honest by construction (Principle 5): the address renders ONLY when the
 * server says email intake is switched on AND names the mailbox
 * (`channels.email` with a non-null `inboundAddress`, see
 * fetchVaxIntakeConfig). Otherwise the card says forwarding is not on yet —
 * it never prints an address that would bounce. While the shell is still
 * asking, it renders nothing rather than flashing the "not on" copy.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface VaxForwardingAddressProps {
  /** The facility's inbound address, or null when email intake is off. */
  address: string | null;
  /** The shell's config probe is still in flight. */
  loading?: boolean;
  /** Seam for tests; defaults to the browser clipboard. */
  writeClipboard?: (text: string) => Promise<void>;
}

type CopyState = 'idle' | 'copied' | 'failed';

/** How long "Copied" stays up. Long enough to read, short enough to re-copy. */
export const COPIED_VISIBLE_MS = 2500;

async function browserClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  throw new Error('clipboard_unavailable');
}

export function VaxForwardingAddress({
  address,
  loading = false,
  writeClipboard = browserClipboard,
}: VaxForwardingAddressProps): JSX.Element | null {
  const [copy, setCopy] = useState<CopyState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const onCopy = useCallback(async () => {
    if (!address) return;
    if (timer.current) clearTimeout(timer.current);
    try {
      await writeClipboard(address);
      setCopy('copied');
      timer.current = setTimeout(() => setCopy('idle'), COPIED_VISIBLE_MS);
    } catch {
      // No clipboard (an insecure context, a denied permission): say so and
      // leave the address selectable — never pretend it was copied.
      setCopy('failed');
    }
  }, [address, writeClipboard]);

  if (loading) return null;

  if (!address) {
    return (
      <section
        className="ferro-vax-forward ferro-vax-forward--off"
        aria-labelledby="vax-forward-title"
        data-testid="vax-forward-off"
      >
        <h2 id="vax-forward-title" className="ferro-vax-forward__label">
          Email forwarding
        </h2>
        <p className="ferro-vax-forward__off-title">
          Email forwarding isn&rsquo;t switched on yet.
        </p>
        <p className="ferro-vax-forward__hint">
          Owners can still upload records in the app, and they land here. Once
          forwarding is on, this is where you&rsquo;ll find the address to give
          vet offices.
        </p>
      </section>
    );
  }

  return (
    <section
      className="ferro-vax-forward"
      aria-labelledby="vax-forward-title"
      data-testid="vax-forward"
    >
      <h2 id="vax-forward-title" className="ferro-vax-forward__label">
        Forwarding address for vet records
      </h2>
      <div className="ferro-vax-forward__row">
        <span className="ferro-vax-forward__address" data-testid="vax-forward-address">
          {/* A phone-width line breaks after the "@", never mid-word
              ("…app.c / om"); <wbr> adds no character, so a copy of the
              selection is still the exact address. */}
          {address.includes('@') ? (
            <>
              {address.slice(0, address.indexOf('@') + 1)}
              <wbr />
              {address.slice(address.indexOf('@') + 1)}
            </>
          ) : (
            address
          )}
        </span>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-vax-forward__copy"
          onClick={() => void onCopy()}
          data-testid="vax-forward-copy"
          aria-describedby="vax-forward-status"
        >
          {copy === 'copied' ? 'Copied' : 'Copy'}
        </button>
      </div>
      {/* Always mounted so screen readers hear the change (a live region
          added at the moment of the announcement is often missed). */}
      <p
        id="vax-forward-status"
        className={`ferro-vax-forward__status${
          copy === 'failed' ? ' ferro-vax-forward__status--failed' : ''
        }`}
        role="status"
        aria-live="polite"
        data-testid="vax-forward-status"
      >
        {copy === 'copied'
          ? 'Copied — paste it into your email to the vet.'
          : copy === 'failed'
            ? 'Couldn’t copy automatically. Select the address and copy it.'
            : ''}
      </p>
      <p className="ferro-vax-forward__hint">
        Give this to vet offices. Records they email here are read
        automatically and wait in this inbox for you to approve.
      </p>
    </section>
  );
}
